import React from 'react';
import { Sparkles, TrendingUp, ShieldCheck, ArrowUpRight } from 'lucide-react';

export interface GlassmorphismCardProps {
  title?: string;
  metric?: string;
  subtitle?: string;
  badge?: string;
  changeRate?: string;
  className?: string;
  children?: React.ReactNode;
}

export const GlassmorphismCard: React.FC<GlassmorphismCardProps> = ({
  title = 'System Analytics',
  metric = '$124,580.00',
  subtitle = 'Real-time production & asset yield',
  badge = 'Active Aurora',
  changeRate = '+18.4%',
  className = '',
  children,
}) => {
  return (
    <div
      className={`relative w-full max-w-md overflow-hidden rounded-3xl p-8 transition-all duration-300 hover:scale-[1.01] ${className}`}
      style={{
        // 60% blur frosted glass with 20% opacity white fill
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        backgroundColor: 'rgba(255, 255, 255, 0.20)',
        // Crisp, razor-thin glowing white border
        border: '1px solid rgba(255, 255, 255, 0.45)',
        boxShadow:
          '0 20px 40px -15px rgba(0, 0, 0, 0.3), 0 0 20px 1px rgba(255, 255, 255, 0.25), inset 0 1px 1px rgba(255, 255, 255, 0.6)',
      }}
    >
      {/* Subtle inner glass specular highlights */}
      <div
        className="pointer-events-none absolute -top-24 -left-24 h-48 w-48 rounded-full bg-white/20 blur-2xl"
        aria-hidden="true"
      />

      {/* Top Header Row */}
      <div className="relative z-10 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/25 border border-white/40 shadow-sm backdrop-blur-md">
            <Sparkles className="h-5 w-5 text-white drop-shadow-[0_2px_4px_rgba(0,0,0,0.35)]" />
          </div>
          <div>
            <span className="text-[11px] font-semibold tracking-wider uppercase text-white/80 drop-shadow-sm">
              {badge}
            </span>
            <h3 className="text-base font-bold text-white tracking-tight drop-shadow-[0_2px_4px_rgba(0,0,0,0.4)]">
              {title}
            </h3>
          </div>
        </div>

        <div className="flex items-center gap-1 rounded-full bg-white/20 px-3 py-1 border border-white/40 backdrop-blur-sm shadow-xs">
          <TrendingUp className="h-3.5 w-3.5 text-emerald-200 drop-shadow-sm" />
          <span className="text-xs font-semibold text-white drop-shadow-sm">{changeRate}</span>
        </div>
      </div>

      {/* Metric Display */}
      <div className="relative z-10 mt-6">
        <div className="text-3xl sm:text-4xl font-extrabold tracking-tight text-white drop-shadow-[0_4px_8px_rgba(0,0,0,0.45)]">
          {metric}
        </div>
        <p className="mt-1 text-xs sm:text-sm font-medium text-white/85 drop-shadow-[0_1px_2px_rgba(0,0,0,0.3)]">
          {subtitle}
        </p>
      </div>

      {/* Action / Detail Section */}
      {children ? (
        <div className="relative z-10 mt-6">{children}</div>
      ) : (
        <div className="relative z-10 mt-6 pt-5 border-t border-white/25 flex items-center justify-between">
          <div className="flex items-center gap-1.5 text-xs text-white/90 drop-shadow-sm">
            <ShieldCheck className="h-4 w-4 text-white/90" />
            <span>Verified Node</span>
          </div>

          <button
            type="button"
            className="flex items-center gap-1 text-xs font-semibold text-white bg-white/20 hover:bg-white/30 border border-white/40 rounded-lg px-3 py-1.5 transition-all shadow-sm"
          >
            <span>View Details</span>
            <ArrowUpRight className="h-3.5 w-3.5" />
          </button>
        </div>
      )}
    </div>
  );
};

export const GlassmorphismShowcase: React.FC = () => {
  return (
    <div className="relative flex min-h-[420px] w-full items-center justify-center overflow-hidden rounded-3xl p-8 shadow-2xl">
      {/* Vibrant dynamic Aurora gradient background */}
      <div
        className="absolute inset-0 bg-gradient-to-tr from-[#3b0764] via-[#0284c7] to-[#ec4899] animate-pulse"
        style={{ animationDuration: '8s' }}
      />
      {/* Secondary fluid color orbs for Aurora wave depth */}
      <div className="pointer-events-none absolute -top-1/4 -right-1/4 h-[500px] w-[500px] rounded-full bg-gradient-to-br from-pink-500/40 via-purple-600/40 to-transparent blur-3xl" />
      <div className="pointer-events-none absolute -bottom-1/4 -left-1/4 h-[500px] w-[500px] rounded-full bg-gradient-to-tr from-cyan-400/40 via-blue-600/40 to-transparent blur-3xl" />

      {/* The frosted glass card hovering on the aurora background */}
      <GlassmorphismCard />
    </div>
  );
};

export default GlassmorphismCard;
